/**
 * Event basics (`/me/events/:id/basic`).
 *
 * The editable half of the event record: title, city, time window and weather
 * condition. Participants and activities have their own child routes because
 * each is a different editing task.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'
import { CITY_KEYS, type CityKey } from '@shared/cities'
import { WEATHER_CONDITIONS, type WeatherCondition } from '@shared/types'
import { isValidLocalDateTime } from '@shared/validation'

import { ErrorAlert } from '../../../components/layout/states'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { Field, FieldGroup } from '../../../components/ui/field'
import { AGGREGATION_POLICIES, NEED_PROFILES, type AggregationPolicy, type NeedProfile } from '@shared/types'
import { Input, Label } from '../../../components/ui/input'
import { useUpdateEvent } from '../../../hooks/use-events'
import { mutationErrorText } from '../../../lib/mutation-error'
import { cityLabelKey } from '../../../lib/format'
import { useEventDetailContext } from './layout'

interface BasicsForm {
  title: string
  city: CityKey
  eventDateStart: string
  eventDateEnd: string
  weatherCondition: WeatherCondition
  /** How a round trades one group's sacrifice against another's. */
  aggregationPolicy: AggregationPolicy
  /** Whether the answer must contain a mall, or only somewhere to sit down and eat. */
  needProfile: NeedProfile
}

export function EventBasicPage() {
  const { t } = useTranslation()
  const { event } = useEventDetailContext()
  const updateEvent = useUpdateEvent(event.id)

  const [form, setForm] = useState<BasicsForm>({
    title: event.title,
    city: event.city,
    eventDateStart: event.eventDateStart,
    eventDateEnd: event.eventDateEnd ?? '',
    weatherCondition: event.weatherCondition,
    aggregationPolicy: event.aggregationPolicy,
    needProfile: event.needProfile,
  })
  const [titleError, setTitleError] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [endError, setEndError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // `updatedAt` is the only signal that the server value actually changed, so a
  // background refetch of the same version cannot overwrite what is being typed.
  useEffect(() => {
    setForm({
      title: event.title,
      city: event.city,
      eventDateStart: event.eventDateStart,
      eventDateEnd: event.eventDateEnd ?? '',
      weatherCondition: event.weatherCondition,
      aggregationPolicy: event.aggregationPolicy,
      needProfile: event.needProfile,
    })
  }, [event.id, event.updatedAt])

  function submit() {
    const title = form.title.trim()
    if (title.length < LIMITS.eventTitleMinLength || title.length > LIMITS.eventTitleMaxLength) {
      setTitleError(t('eventNew.titleError'))
      setSaved(false)
      return
    }
    if (!isValidLocalDateTime(form.eventDateStart)) {
      setTitleError(null)
      setStartError(t('eventNew.dateError'))
      setSaved(false)
      return
    }
    // An end that is not after the start describes no window: the engine would read
    // the resulting non-positive length as "no constraint" rather than as a mistake.
    // The message is attached to the end field, because that value is at fault.
    if (form.eventDateEnd.length > 0 && form.eventDateEnd <= form.eventDateStart) {
      setTitleError(null)
      setStartError(null)
      setEndError(t('eventNew.dateOrderError'))
      setSaved(false)
      return
    }
    setTitleError(null)
    setStartError(null)
    setEndError(null)

    updateEvent.mutate(
      {
        title,
        city: form.city,
        eventDateStart: form.eventDateStart,
        eventDateEnd: form.eventDateEnd.length > 0 ? form.eventDateEnd : null,
        weatherCondition: form.weatherCondition,
        aggregationPolicy: form.aggregationPolicy,
        needProfile: form.needProfile,
      },
      { onSuccess: () => setSaved(true) },
    )
  }

  const errorText = mutationErrorText(updateEvent.error, t)

  return (
    <Card>
      <CardContent className="pt-4 sm:pt-5">
        <form
          className="flex flex-col gap-4"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault()
            submit()
          }}
        >
          <FieldGroup>
            {/* The two product switches: how the group is balanced, and whether the
                answer has to be a shopping centre at all. */}
            <Field label={t('policy.title')} htmlFor="aggregation-policy">
              <select
                id="aggregation-policy"
                className="border-input bg-background h-9 rounded-md border px-3 text-sm"
                value={form.aggregationPolicy}
                onChange={(changeEvent) =>
                  setForm({ ...form, aggregationPolicy: changeEvent.target.value as AggregationPolicy })
                }
              >
                {AGGREGATION_POLICIES.map((policy) => (
                  <option key={policy} value={policy}>
                    {t(`policy.${policy}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('policy.needLabel')} htmlFor="need-profile">
              <select
                id="need-profile"
                className="border-input bg-background h-9 rounded-md border px-3 text-sm"
                value={form.needProfile}
                onChange={(changeEvent) =>
                  setForm({ ...form, needProfile: changeEvent.target.value as NeedProfile })
                }
              >
                {NEED_PROFILES.map((profile) => (
                  <option key={profile} value={profile}>
                    {t(`policy.need.${profile}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label={t('eventNew.titleLabel')}
              htmlFor="event-title"
              required
              error={titleError}
              hint={t('eventNew.titleHint')}
            >
              <Input
                id="event-title"
                value={form.title}
                maxLength={LIMITS.eventTitleMaxLength}
                aria-invalid={Boolean(titleError)}
                onChange={(inputEvent) =>
                  setForm((current) => ({ ...current, title: inputEvent.target.value }))
                }
              />
            </Field>

            <div className="flex flex-col gap-2">
              <Label htmlFor="event-city">{t('eventNew.cityLabel')}</Label>
              <select
                id="event-city"
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm shadow-sm"
                value={form.city}
                onChange={(selectEvent) =>
                  setForm((current) => ({ ...current, city: selectEvent.target.value as CityKey }))
                }
              >
                {CITY_KEYS.map((city) => (
                  <option key={city} value={city}>
                    {t(cityLabelKey(city))}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label={t('eventNew.dateStartLabel')}
              htmlFor="event-start"
              required
              error={startError}
            >
              <Input
                id="event-start"
                type="datetime-local"
                value={form.eventDateStart}
                onChange={(inputEvent) => {
                  setForm((current) => ({ ...current, eventDateStart: inputEvent.target.value }))
                  setStartError(null)
                }}
              />
            </Field>

            <Field label={t('eventNew.dateEndLabel')} htmlFor="event-end" error={endError}>
              <Input
                id="event-end"
                type="datetime-local"
                value={form.eventDateEnd}
                onChange={(inputEvent) => {
                  setForm((current) => ({ ...current, eventDateEnd: inputEvent.target.value }))
                  setEndError(null)
                }}
              />
            </Field>

            <div className="flex flex-col gap-2">
              <Label>{t('eventNew.weatherLabel')}</Label>
              <ChipGroup label={t('eventNew.weatherLabel')}>
                {WEATHER_CONDITIONS.map((condition) => (
                  <Chip
                    key={condition}
                    selected={form.weatherCondition === condition}
                    onClick={() =>
                      setForm((current) => ({ ...current, weatherCondition: condition }))
                    }
                  >
                    {t(`weather.${condition}`)}
                  </Chip>
                ))}
              </ChipGroup>
              <p className="text-muted-foreground text-xs">{t('eventNew.weatherHint')}</p>
            </div>
          </FieldGroup>

          {errorText ? (
            <ErrorAlert message={errorText} />
          ) : null}

          {saved ? (
            <Alert variant="success">
              <AlertDescription>{t('profile.saved')}</AlertDescription>
            </Alert>
          ) : null}

          <Button type="submit" disabled={updateEvent.isPending}>
            {t('common.save')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
